import { useEffect, useState } from "react";
import { articles } from "../../../mocks/articles";
import { ArticleCard } from "./ArticleCard";

const filters = [
  { key: "all", name: "全部" },
  { key: "headline", name: "头条解读" },
  { key: "analysis", name: "重点瓜分析" },
  { key: "deep", name: "深度报道" },
  { key: "cover", name: "封面故事" },
  { key: "pick", name: "编辑推荐" },
];

const tags = ["品牌舆情", "演出市场", "综艺产业", "媒体生态", "剧集市场", "公关行业", "人物专题"];

export default function ContentFeed() {
  const [active, setActive] = useState("all");
  const [activeTag, setActiveTag] = useState("");
  const [keyword, setKeyword] = useState("");
  const [searched, setSearched] = useState("");
  const [visibleCount, setVisibleCount] = useState(6);

  // 监听导航栏搜索与栏目跳转事件
  useEffect(() => {
    const onSearch = (e: Event) => {
      const kw = (e as CustomEvent<string>).detail;
      setSearched(kw);
      setKeyword(kw);
      setActive("all");
      setActiveTag("");
      setVisibleCount(6);
    };
    const onColumn = (e: Event) => {
      const key = (e as CustomEvent<string>).detail;
      setActive(key);
      setSearched("");
      setKeyword("");
      setActiveTag("");
      setVisibleCount(6);
    };
    window.addEventListener("guabao:search", onSearch);
    window.addEventListener("guabao:column", onColumn);
    return () => {
      window.removeEventListener("guabao:search", onSearch);
      window.removeEventListener("guabao:column", onColumn);
    };
  }, []);

  const kw = searched.trim().toLowerCase();
  let list = articles;
  if (active !== "all") list = list.filter((a) => a.category === active);
  if (activeTag) list = list.filter((a) => a.tag === activeTag);
  if (kw) list = list.filter((a) => (a.title + a.excerpt + a.tag).toLowerCase().includes(kw));
  list = list.slice(0, visibleCount);

  return (
    <section id="feed" className="scroll-mt-24 bg-background-100/70">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <p className="section-kicker text-xs font-semibold text-primary-700">今日瓜报 · 内容中心</p>
            <h2 className="mt-2 font-serif text-2xl font-black text-foreground-950 md:text-3xl">最新热稿</h2>
            <p className="mt-2 text-sm text-foreground-600">按栏目浏览，或用关键词快速定位你想看的那颗“瓜”。</p>
          </div>
          <form
            className="flex w-full max-w-md items-center rounded-full bg-background-50 border border-background-200 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-200"
            onSubmit={(e) => {
              e.preventDefault();
              setSearched(keyword.trim());
              setVisibleCount(6);
            }}
          >
            <i className="ri-search-line pl-4 text-base text-foreground-500" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索标题 / 栏目 / 关键词…"
              aria-label="搜索文章"
              className="h-11 flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="mr-1 whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-xs text-background-50 hover:bg-primary-600 transition-colors">
              搜索
            </button>
          </form>
        </div>

        {/* 栏目筛选 */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-full bg-background-50 p-1">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => {
                  setActive(f.key);
                  setActiveTag("");
                  setVisibleCount(6);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  active === f.key ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                {f.name}
              </button>
            ))}
          </div>
          <span className="mx-1 hidden text-background-300 sm:block">|</span>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <button
                key={t}
                onClick={() => {
                  setActiveTag(activeTag === t ? "" : t);
                  setActive("all");
                  setVisibleCount(6);
                }}
                className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${
                  activeTag === t
                    ? "border-accent-500 bg-accent-500 text-background-50"
                    : "border-background-200 bg-background-50 text-foreground-600 hover:border-accent-300"
                }`}
              >
                #{t}
              </button>
            ))}
          </div>
        </div>

        {/* 搜索提示 */}
        {kw && (
          <p className="mt-5 text-sm text-foreground-600">
            为你找到与 <strong className="text-primary-700">“{searched}”</strong> 相关的 {list.length} 篇内容：
          </p>
        )}

        {/* 卡片流 */}
        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a: (typeof articles)[number], i: number) => (
              <ArticleCard key={a.slug} article={a} showImage={true} className="rounded-xl bg-background-50 p-4 shadow-soft" />
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <i className="ri-emotion-sad-line text-5xl text-secondary-300" aria-hidden="true"></i>
            <p className="mt-4 font-serif text-lg font-bold text-foreground-800">没有找到相关内容</p>
            <p className="mt-1 text-sm text-foreground-500">换个栏目或关键词试试吧</p>
            <button
              onClick={() => {
                setActive("all");
                setActiveTag("");
                setSearched("");
                setKeyword("");
                setVisibleCount(6);
              }}
              className="mt-5 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600 transition-colors"
            >
              查看全部
            </button>
          </div>
        )}

        {visibleCount < articles.length && list.length > 0 && (
          <div className="mt-10 text-center">
            <button
              onClick={() => setVisibleCount((v) => v + 6)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-foreground-950 px-6 py-2.5 text-sm font-medium text-foreground-900 hover:bg-foreground-950 hover:text-background-50 transition-colors"
            >
              加载更多 <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}